import React from 'react'

import { useMap } from '@vis.gl/react-google-maps'

import { AppBar, Select, MenuItem, Typography, Stack, FormControl, CircularProgress } from '@mui/material'
import { SelectChangeEvent } from '@mui/material/Select'

import { Station } from '@/types/Station'

import { haversineDistance } from '@/utils/map'
import { numericUtils } from '@/utils/numeric'

interface SearchBarProps {
  stations: Station[]
  isLoadingStations: boolean
  selectedStation: Station | null
  setSelectedStation: (value: Station | null) => void
  userCoords?: { lat: number; lng: number } | null
}

const SearchBar: React.FC<SearchBarProps> = ({
  stations,
  isLoadingStations,
  selectedStation,
  setSelectedStation,
  userCoords,
}) => {
  const map = useMap()

  const handleOnChange = (event: SelectChangeEvent<number>) => {
    const selectedId = Number(event.target.value) as number
    const station = stations.find((s) => s.id === selectedId) || null

    if (station && map) {
      map.moveCamera({
        center: {
          lat: station.latitude,
          lng: station.longitude,
        },
        zoom: 15,
      })
    }

    setSelectedStation(station)
  }

  const StationOption: React.FC<{
    station: Station
  }> = ({ station }) => {
    const distanceKm = userCoords
      ? haversineDistance(userCoords, { lat: station.latitude, lng: station.longitude }) / 1000
      : null

    return (
      <Stack direction="column" spacing={0.25} sx={{ width: '100%', paddingY: 1 }}>
        <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {station.name}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {station.address}
          {!!distanceKm && <>{` (${numericUtils.formatNumberToBRLDecimal(distanceKm)} km)`}</>}
        </Typography>
      </Stack>
    )
  }

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        top: '48px',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'transparent',
        paddingTop: 'env(safe-area-inset-top)',
        paddingX: '24px',
        width: '100%',
      }}
    >
      <FormControl
        variant="outlined"
        size="small"
        fullWidth
        sx={{
          padding: 1,
          borderRadius: 4,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          boxShadow: 4,

          '& fieldset': {
            border: 'none',
          },
        }}
      >
        <Select
          labelId="station-select-label"
          value={selectedStation ? selectedStation.id : ''}
          onChange={handleOnChange}
          displayEmpty
          renderValue={(selected) => {
            if (!selected) {
              return (
                <Typography variant="body1" color="text.secondary">
                  Selecione uma estação de recarga
                </Typography>
              )
            }
            const station = stations.find((s) => s.id === selected)
            return station ? station.name : ''
          }}
          MenuProps={{
            MenuListProps: {
              sx: { padding: 0 },
            },
            PaperProps: {
              elevation: 0,
              sx: {
                borderRadius: 4,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                maxHeight: '50svh',
                boxShadow: 4,
              },
            },
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: -15,
              horizontal: 'left',
            },
          }}
          sx={{
            '.MuiSelect-icon': {
              color: 'inherit',
            },
          }}
        >
          {isLoadingStations ? (
            <MenuItem disabled>
              <Stack direction="row" alignItems="center" justifyContent="center" flex={1} spacing={1}>
                <CircularProgress size={20} />
                <Typography variant="body2">Carregando...</Typography>
              </Stack>
            </MenuItem>
          ) : stations.length > 0 ? (
            stations.map((station) => (
              <MenuItem key={station.id} value={station.id} sx={{ borderBottom: '1px solid #e0e0e0' }}>
                <StationOption station={station} />
              </MenuItem>
            ))
          ) : (
            <MenuItem disabled>
              <Typography variant="body2" color="text.secondary">
                Nenhum resultado encontrado
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </AppBar>
  )
}

export default SearchBar
